<template>
  <div class="copilot-chat-root">
    <section class="copilot-chat-card">
      <header class="copilot-chat-header">
        <div class="copilot-chat-header-main">
          <div class="copilot-chat-avatar" aria-hidden="true">
            <img
              src="/static/icons/Alan.png"
              alt=""
              class="copilot-chat-avatar-img"
            />
          </div>

          <div class="copilot-chat-heading">
            <h3>{{ $t('complaintChatHeaderTitle') }}</h3>
            <p>{{ $t('complaintChatHeaderDescription') }}</p>
          </div>
        </div>
      </header>

      <div class="copilot-webchat-container">
        <div
          v-if="isLoading"
          class="copilot-chat-message"
          aria-live="polite"
        >
          {{ $t('complaintChatConnecting') }}
        </div>

        <div
          v-else-if="errorMessage"
          class="copilot-chat-message copilot-chat-message--error"
          role="alert"
        >
          {{ errorMessage }}
        </div>

        <div
          v-show="!isLoading && !errorMessage"
          ref="chatContainer"
          class="copilot-webchat-host"
        ></div>

        <div
          v-if="
            !isLoading && !errorMessage && isWaitingForInitialMessage
          "
          class="copilot-initial-message-loader"
          aria-live="polite"
        >
          <span
            class="copilot-initial-message-spinner"
            aria-hidden="true"
          ></span>
          <span>{{ $t('complaintChatPreparing') }}</span>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import Api from '@/services/Api';

const WEB_CHAT_CDN_URL =
  'https://cdn.botframework.com/botframework-webchat/4.17.0/webchat.js';

const styleOptions = {
  accent: '#04509f',
  backgroundColor: '#FFFFFF',
  bubbleBackground: '#FFFFFF',
  bubbleBorderColor: '#E0E0E0',
  bubbleBorderRadius: 10,
  bubbleBorderStyle: 'solid',
  bubbleBorderWidth: 1,
  bubbleMaxWidth: 620,
  bubbleMinWidth: 44,
  bubbleTextColor: '#3E3C44',
  bubbleNubOffset: 12,
  bubbleNubSize: 8,
  bubbleFromUserBackground: '#EBF2FA',
  bubbleFromUserBorderColor: '#00346A',
  bubbleFromUserBorderRadius: 10,
  bubbleFromUserTextColor: '#3E3C44',
  bubbleFromUserNubOffset: 12,
  bubbleFromUserNubSize: 8,
  bubbleFromUserMaxWidth: 620,
  botAvatarInitials: '',
  botAvatarImage: '/static/icons/Alan.png',
  botAvatarBackgroundColor: '#FFFFFF',
  avatarSize: 54,
  userAvatarInitials: '',
  userAvatarBackgroundColor: 'transparent',
  sendBoxBackground: '#FFFFFF',
  sendBoxTextColor: '#3E3C44',
  sendBoxButtonColor: '#3E3C44',
  sendBoxButtonColorOnHover: '#04509F',
  sendBoxBorderTop: '0',
  sendBoxHeight: 48,
  hideUploadButton: false,
  uploadMultiple: false,
  timestampColor: '#9A98A2',
  timestampFormat: 'absolute',
  suggestedActionBackgroundColor: '#FFFFFF',
  suggestedActionBorderColor: '#0057A8',
  suggestedActionTextColor: '#0057A8',
};

let webChatCdnPromise = null;

function loadWebChatCdn() {
  if (window.WebChat) {
    return Promise.resolve(window.WebChat);
  }

  if (webChatCdnPromise) {
    return webChatCdnPromise;
  }

  webChatCdnPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WEB_CHAT_CDN_URL;
    script.onload = () => resolve(window.WebChat);
    script.onerror = (error) => {
      webChatCdnPromise = null;
      reject(error);
    };
    document.head.appendChild(script);
  });

  return webChatCdnPromise;
}

export default {
  name: 'CopilotAgentChat',

  props: {
    correoContacto: {
      type: String,
      default: '',
    },
    codigoCliente: {
      type: String,
      default: '',
    },
    codigoDireccion: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      errorMessage: '',
      hasInitializedWebChat: false,
      isLoading: true,
      webchatCleanup: null,
      directLine: null,
      initialEventsTimeoutId: null,
      isWaitingForInitialMessage: true,
      isWebChatInitialized: false,
      timestampObserver: null,
      timestampFrameId: null,
    };
  },

  computed: {
    codIdioma() {
      const languageInterface =
        this.$store.state.user &&
        this.$store.state.user.languageInterface;

      return (languageInterface || '').trim() || 'es';
    },
  },

  mounted() {
    this.initializeWebChat();
  },

  beforeDestroy() {
    this.stopTimestampObserver();

    if (this.initialEventsTimeoutId) {
      clearTimeout(this.initialEventsTimeoutId);
      this.initialEventsTimeoutId = null;
    }

    if (
      this.directLine &&
      typeof this.directLine.end === 'function'
    ) {
      this.directLine.end();
    }

    this.directLine = null;

    if (this.webchatCleanup) {
      this.webchatCleanup();
      this.webchatCleanup = null;
    }
  },

  methods: {
    async initializeWebChat() {
      if (this.hasInitializedWebChat) {
        return;
      }
      this.isWebChatInitialized = true;

      try {
        this.isLoading = true;
        this.errorMessage = '';
        this.isWaitingForInitialMessage = true;

        const requestedUserID = this.getStableWebChatUserId();
        const directLineConfig =
          await Api.post_copilot_directline_token(
            this.codigoCliente,
            this.codigoDireccion,
            requestedUserID
          );

        await loadWebChatCdn();

        const userID = directLineConfig.userId;
        const directLine = window.WebChat.createDirectLine({
          token: directLineConfig.token,
          domain: directLineConfig.domain,
        });
        this.directLine = directLine;
        const store = this.createWebChatStore(userID);

        this.isLoading = false;
        await this.$nextTick();

        if (!this.$refs.chatContainer) {
          return;
        }

        window.WebChat.renderWebChat(
          {
            directLine: this.directLine,
            store,
            styleOptions: {
              ...styleOptions,
              sendBoxPlaceholder: this.$t('complaintChatPlaceholder'),
            },
            locale: 'es-ES',
            userID,
            username: this.correoContacto || '',
          },
          this.$refs.chatContainer
        );
        this.startTimestampObserver();
        this.$nextTick(() => {
          this.normalizeWebChatTimestamps();
        });

        this.webchatCleanup = () => {
          if (this.$refs.chatContainer) {
            this.$refs.chatContainer.innerHTML = '';
          }
        };

        this.hasInitializedWebChat = true;
      } catch (error) {
        this.isWebChatInitialized = false;
        this.isWaitingForInitialMessage = false;
        this.errorMessage = this.$t(
          'complaintChatInitializationError'
        );
        this.isLoading = false;
      }
    },

    normalizeWebChatTimestamps() {
      if (!this.$refs.chatContainer) {
        return;
      }

      const timestampNodes =
        this.$refs.chatContainer.querySelectorAll(
          '.webchat__timestamp, .webchat__stacked-layout__status'
        );

      timestampNodes.forEach((node) => {
        if (
          node.classList.contains('webchat__stacked-layout__status') &&
          node.querySelector('.webchat__timestamp')
        ) {
          return;
        }

        const rawText = (node.textContent || '').trim();

        if (node.dataset.copilotTimeSource === node.textContent) {
          return;
        }

        node.dataset.copilotTimeSource = node.textContent;

        const normalizedTime = this.extractTimeFromStatus(rawText);

        if (normalizedTime && rawText !== normalizedTime) {
          node.setAttribute('data-copilot-time', normalizedTime);
        } else {
          node.removeAttribute('data-copilot-time');
        }
      });

      this.markWebChatAttachmentMessages();
    },

    markWebChatAttachmentMessages() {
      if (!this.$refs.chatContainer) {
        return;
      }

      const botLabel = this.$t('complaintChatBotName');
      const userLabel = this.$t('complaintChatUserLabel');
      const attachmentSelector = [
        '.webchat__attachment',
        '.webchat__imageContent',
        '.webchat__fileContent',
        '.webchat__bubble__content img',
        '.ac-container img',
        'img.ac-image',
      ].join(', ');

      this.$refs.chatContainer
        .querySelectorAll(
          '.webchat__stacked-layout:not(.webchat__stacked-layout--from-user)'
        )
        .forEach((messageNode) => {
          const contentNode = messageNode.querySelector(
            '.webchat__stacked-layout__content'
          );

          if (contentNode) {
            contentNode.setAttribute('data-bot-label', botLabel);
          }
        });

      this.$refs.chatContainer
        .querySelectorAll('.webchat__stacked-layout--from-user')
        .forEach((messageNode) => {
          const contentNode = messageNode.querySelector(
            '.webchat__stacked-layout__content'
          );

          if (contentNode) {
            contentNode.setAttribute('data-user-label', userLabel);
          }

          if (messageNode.querySelector(attachmentSelector)) {
            messageNode.classList.add(
              'copilot-webchat-user-attachment'
            );
          } else {
            messageNode.classList.remove(
              'copilot-webchat-user-attachment'
            );
          }
        });
    },

    extractTimeFromStatus(text) {
      const value = (text || '').trim();

      const match = value.match(
        /(\d{1,2}):(\d{2})(?:\s*(a\.?\s*m\.?|p\.?\s*m\.?|AM|PM))?/i
      );

      if (!match) {
        return '';
      }

      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const meridian = (match[3] || '')
        .toLowerCase()
        .replace(/\./g, '')
        .replace(/\s/g, '');

      if ((meridian === 'pm' || meridian === 'p m') && hours < 12) {
        hours += 12;
      }

      if ((meridian === 'am' || meridian === 'a m') && hours === 12) {
        hours = 0;
      }

      return `${String(hours).padStart(2, '0')}:${minutes}`;
    },

    startTimestampObserver() {
      this.stopTimestampObserver();

      if (!this.$refs.chatContainer) {
        return;
      }

      this.timestampObserver = new MutationObserver(() => {
        if (this.timestampFrameId) {
          return;
        }

        this.timestampFrameId = window.requestAnimationFrame(() => {
          this.timestampFrameId = null;
          this.normalizeWebChatTimestamps();
        });
      });

      this.timestampObserver.observe(this.$refs.chatContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    },

    stopTimestampObserver() {
      if (this.timestampObserver) {
        this.timestampObserver.disconnect();
        this.timestampObserver = null;
      }

      if (this.timestampFrameId) {
        window.cancelAnimationFrame(this.timestampFrameId);
        this.timestampFrameId = null;
      }
    },

    getStableWebChatUserId() {
      const storageKey = [
        'copilot_webchat_user',
        this.codigoCliente || 'anon',
        this.codigoDireccion || 'default',
      ].join('_');

      let userId = window.sessionStorage.getItem(storageKey);

      if (!userId) {
        const randomPart =
          window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

        userId = `dl_${randomPart}`;
        window.sessionStorage.setItem(storageKey, userId);
      }

      if (!userId.startsWith('dl_')) {
        const randomPart =
          window.crypto && window.crypto.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

        userId = `dl_${randomPart}`;
        window.sessionStorage.setItem(storageKey, userId);
      }

      return userId;
    },

    createWebChatStore(userID) {
      let hasSentInitialEvents = false;

      return window.WebChat.createStore(
        {},
        ({ dispatch }) =>
          (next) =>
          (action) => {
            let currentAction = action;
            const activity =
              currentAction &&
              currentAction.payload &&
              currentAction.payload.activity;

            const isIncomingBotActivity =
              currentAction.type ===
                'DIRECT_LINE/INCOMING_ACTIVITY' &&
              activity &&
              activity.from &&
              activity.from.id !== userID;

            if (isIncomingBotActivity) {
              currentAction = {
                ...currentAction,
                payload: {
                  ...currentAction.payload,
                  activity: {
                    ...activity,
                    from: {
                      ...activity.from,
                      name: this.$t('complaintChatBotName'),
                    },
                  },
                },
              };
            }

            const shouldHideInitialMessageLoader =
              this.isFirstBotMessage(currentAction, userID);

            if (
              currentAction.type ===
                'DIRECT_LINE/CONNECT_FULFILLED' &&
              !hasSentInitialEvents
            ) {
              const result = next(currentAction);

              hasSentInitialEvents = true;

              setTimeout(() => {
                this.sendInitialEvents(dispatch);
              }, 0);

              return result;
            }

            const result = next(currentAction);

            if (shouldHideInitialMessageLoader) {
              setTimeout(() => {
                this.isWaitingForInitialMessage = false;
              }, 0);
            }

            return result;
          }
      );
    },

    isFirstBotMessage(action, userID) {
      const activity = action.payload && action.payload.activity;

      return (
        this.isWaitingForInitialMessage &&
        action.type === 'DIRECT_LINE/INCOMING_ACTIVITY' &&
        activity &&
        activity.type === 'message' &&
        activity.from &&
        activity.from.id !== userID &&
        activity.from.role !== 'user'
      );
    },

    sendInitialEvents(dispatch) {
      dispatch({
        type: 'WEB_CHAT/SEND_EVENT',
        payload: {
          name: 'pvaSetContext',
          value: {
            correoContacto: this.correoContacto,
            codigoCliente: this.codigoCliente,
            codIdioma: this.codIdioma,
          },
        },
      });

      setTimeout(() => {
        dispatch({
          type: 'WEB_CHAT/SEND_EVENT',
          payload: {
            name: 'startConversation',
            value: '',
          },
        });
        this.initialEventsTimeoutId = null;
      }, 800);
    },
  },
};
</script>

<style scoped>
.copilot-chat-root {
  width: 100%;
  height: 100%;
}

.copilot-chat-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;

  background: radial-gradient(
      circle at top left,
      rgba(4, 80, 159, 0.06),
      transparent 34%
    ),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #e3edf8;
  border-radius: 22px;
  box-shadow: 0 14px 38px rgba(0, 52, 106, 0.08);
}

.copilot-chat-header {
  display: flex;
  align-items: center;
  gap: 24px;

  min-height: 122px;
  padding: 18px 24px 16px;

  background: linear-gradient(90deg, #f4f8ff 0%, #edf4ff 100%);
  border-bottom: 1px solid #dfeaf7;
  color: #04509f;
}

.copilot-chat-header-main {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
  flex: 1;
}

.copilot-chat-avatar {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 104px;
  height: 104px;
  flex: 0 0 104px;

  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(4, 80, 159, 0.08);
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba(0, 52, 106, 0.12);
}

.copilot-chat-avatar-img {
  display: block;
  width: 84px;
  height: 84px;
  object-fit: contain;
}

.copilot-chat-heading {
  min-width: 0;
  flex: 1;
}

.copilot-chat-heading h3 {
  margin: 0;

  color: #04509f;
  font-size: 23px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
}

.copilot-chat-heading p {
  max-width: none;
  margin: 8px 0 0;

  color: #395778;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
}

.copilot-chat-ornament {
}

.copilot-webchat-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;

  background: transparent;
}

.copilot-chat-message {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
  padding: 16px;

  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.copilot-chat-message--error {
  color: #b42318;
}

.copilot-webchat-host,
.copilot-webchat-host > * {
  width: 100% !important;
  height: 100% !important;
}

.copilot-initial-message-loader {
  position: absolute;
  inset: 0;
  z-index: 2;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  color: #1f2937;
  font-size: 14px;
  font-weight: 600;

  background: rgba(248, 251, 255, 0.86);
  backdrop-filter: blur(1px);
}

.copilot-initial-message-spinner {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;

  border: 2px solid rgba(0, 87, 168, 0.18);
  border-top-color: #0057a8;
  border-radius: 50%;

  animation: copilotInitialSpinner 0.75s linear infinite;
}

.copilot-webchat-host /deep/ .webchat__defaultAvatar {
  overflow: hidden;
  background: #ffffff !important;
  border: 1px solid #e0e0e0;
  border-radius: 50% !important;
  box-shadow: 0 2px 8px rgba(62, 60, 68, 0.28);
}

.copilot-webchat-host ::v-deep .webchat__basic-transcript {
  background: transparent;
  display: flex;
  flex-direction: column;
}

.copilot-webchat-host
  ::v-deep
  .webchat__basic-transcript__scrollable {
  width: 100%;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  padding: 18px 42px 12px 30px;
}

.copilot-webchat-host ::v-deep .webchat__stacked-layout {
  margin: 8px 0;
}

.copilot-webchat-host
  ::v-deep
  .webchat__stacked-layout__avatar-gutter {
  width: 60px !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout:not(.webchat__stacked-layout--from-user)
  .webchat__stacked-layout__avatar-gutter {
  align-items: flex-start !important;
  padding-top: 0 !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout:not(.webchat__stacked-layout--from-user)
  .webchat__defaultAvatar {
  margin-top: 0 !important;
}

.copilot-webchat-host ::v-deep .webchat__stacked-layout__main {
  max-width: calc(100% - 60px);
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__main {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-end !important;

  width: fit-content !important;
  max-width: calc(100% - 88px) !important;

  margin-left: auto !important;
  margin-right: 28px !important;

  overflow: visible !important;
}

.copilot-webchat-host ::v-deep .webchat__stacked-layout__status {
  color: #9a98a2 !important;
  font-size: 12px !important;
  line-height: 15px !important;
  margin-top: 6px !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout:not(.webchat__stacked-layout--from-user)
  .webchat__stacked-layout__status {
  margin-left: 68px !important;
}

.copilot-webchat-host ::v-deep .webchat__bubble {
  overflow: visible !important;
}

.copilot-webchat-host ::v-deep .webchat__bubble__content {
  box-shadow: none !important;
  padding: 2px 4px !important;
  border-radius: 12px !important;
}

.copilot-webchat-host ::v-deep .webchat__bubble__nub {
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.copilot-webchat-host ::v-deep .webchat__send-box {
  padding: 16px 24px 20px;
  background: transparent !important;
}

.copilot-webchat-host ::v-deep .webchat__send-box__main {
  min-height: 56px;
  align-items: center;
  padding: 7px 12px 7px 16px;
  border: 1px solid #04509f;
  border-top: 1px solid #04509f !important;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 52, 106, 0.08);
}

.copilot-webchat-host ::v-deep .webchat__send-box-text-box,
.copilot-webchat-host ::v-deep .webchat__send-box-text-box__input {
  color: #3e3c44 !important;
  font-size: 14px !important;
  line-height: 20px !important;
}

.copilot-webchat-host
  ::v-deep
  .webchat__send-box-text-box__input::placeholder {
  color: #9a98a2 !important;
}

.copilot-webchat-host ::v-deep .webchat__icon-button {
  border-radius: 10px !important;
}

.copilot-webchat-host ::v-deep .webchat__icon-button:hover {
  background: #ebf2fa !important;
}

.copilot-webchat-host ::v-deep .webchat__send-button svg,
.copilot-webchat-host ::v-deep .webchat__upload-button svg {
  fill: #3e3c44 !important;
}

.copilot-webchat-host ::v-deep .webchat__send-button:hover svg,
.copilot-webchat-host ::v-deep .webchat__upload-button:hover svg {
  fill: #04509f !important;
}

.copilot-webchat-host ::v-deep .webchat__bubble__content,
.copilot-webchat-host ::v-deep .webchat__bubble__content p,
.copilot-webchat-host ::v-deep .webchat__text-content,
.copilot-webchat-host ::v-deep .webchat__text-content__markdown,
.copilot-webchat-host ::v-deep .webchat__text-content__markdown p {
  color: #3e3c44 !important;
  font-size: 15px !important;
  line-height: 1.6 !important;
}

@media (max-width: 768px) {
  .copilot-chat-header {
    min-height: auto;
    padding: 14px 16px;
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .copilot-chat-header-main {
    align-items: flex-start;
  }

  .copilot-chat-avatar {
    width: 88px;
    height: 88px;
    flex: 0 0 88px;
  }

  .copilot-chat-avatar-img {
    width: 70px;
    height: 70px;
  }

  .copilot-chat-heading h3 {
    font-size: 20px;
    line-height: 1.22;
  }

  .copilot-chat-heading p {
    font-size: 13px;
  }

  .copilot-webchat-container {
    height: 100%;
  }

  .copilot-webchat-host
    ::v-deep
    .webchat__basic-transcript__scrollable {
    padding: 12px 16px;
  }

  .copilot-webchat-host ::v-deep .webchat__send-box {
    padding: 16px 16px 14px;
  }
}

/* Etiqueta del asistente encima de los mensajes del bot */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout:not(.webchat__stacked-layout--from-user)
  .webchat__stacked-layout__content::before {
  content: attr(data-bot-label);
  display: block;
  margin-bottom: 6px;

  color: #04509f;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: left;
}

@keyframes copilotInitialSpinner {
  to {
    transform: rotate(360deg);
  }
}

/* Etiqueta "TÃƒÆ’Ã‚Âº:" encima de los mensajes del usuario */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__content::before {
  content: attr(data-user-label);
  display: block;
  margin-bottom: 6px;

  color: #04509f;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: right;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__content {
  text-align: right !important;
}

/* Hora del usuario debajo del mensaje, no pegada al borde derecho */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__status {
  display: block !important;

  width: fit-content !important;
  min-width: 44px !important;
  max-width: none !important;

  margin-top: 6px !important;
  margin-left: auto !important;
  margin-right: 28px !important;

  padding-right: 0 !important;

  color: #9a98a2 !important;
  font-size: 12px !important;
  line-height: 15px !important;
  text-align: right !important;
  white-space: nowrap !important;

  overflow: visible !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__bubble,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__bubble__content {
  width: auto !important;
  max-width: 520px !important;

  height: auto !important;
  max-height: none !important;

  overflow: visible !important;
}

/* Adjuntos del usuario: ancho propio sin condicionar las burbujas de texto */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__stacked-layout__main {
  width: 520px !important;
  max-width: calc(100% - 96px) !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__stacked-layout__content,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__bubble,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__bubble__content {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__attachment,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__imageContent,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__fileContent,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .ac-container {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;

  height: auto !important;
  max-height: none !important;

  overflow: visible !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__imageContent
  img,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .webchat__fileContent
  img,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  .ac-container
  img,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user.copilot-webchat-user-attachment
  img.ac-image {
  display: block !important;

  width: 100% !important;
  max-width: 100% !important;

  height: auto !important;
  max-height: none !important;

  object-fit: contain !important;
}

/* Texto del usuario alineado a la izquierda dentro de la burbuja */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__bubble__content,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__text-content,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__text-content__markdown,
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__text-content__markdown
  p {
  text-align: left !important;
}

/* Ocultar la pestaÃƒÆ’Ã‚Â±a/nub del mensaje del usuario, que ahora parece un "7" */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__bubble__nub {
  display: none !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__content::before {
  text-align: right !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__stacked-layout__status {
  margin-left: auto !important;
  margin-right: 28px !important;
  text-align: right !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout--from-user
  .webchat__bubble__nub {
  display: block !important;
}

/* Muestra solo la hora (HH:MM) ocultando el texto original con CSS,
   sin reescribir el DOM que controla React.
   Debe ir al final: hay reglas !important de igual especificidad arriba. */
.copilot-webchat-host
  /deep/
  .webchat__stacked-layout__status[data-copilot-time],
.copilot-webchat-host /deep/ .webchat__timestamp[data-copilot-time] {
  font-size: 0 !important;
}

.copilot-webchat-host
  /deep/
  .webchat__stacked-layout__status[data-copilot-time]::after,
.copilot-webchat-host
  /deep/
  .webchat__timestamp[data-copilot-time]::after {
  content: attr(data-copilot-time);

  color: #9a98a2;
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
}
</style>
